import React, { useState, useEffect, useMemo } from "react";
import empty from "is-empty";
import { useSelector, useDispatch } from "react-redux";

import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsActions } from "src/redux/Settings/actions";

import styles from "./styles.module.scss";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import {
    fundDefaultColDef,
    getDetailFundColumnDefs,
    getCreateFundColumnDefs,
} from "./column_defs";
import Button from "src/components/common/Button";
import { store } from "src/index";
import * as Validators from "src/utils/validators";
import { FUND_SETTING_ERRORS } from "src/redux/Settings/constants";
import { ModalActions } from "src/redux/Modal/actions";

class DeleteButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.deleteBtn;
        this.element = document.createElement("div");
        this.element.className = `${styles.delete} delete-btn`;
        // setAttribute keeps the (user-authored) fund name inert - never parsed as HTML.
        this.element.setAttribute("data-testid", `delete-${params.data?.fundName}`);
        this.element.onclick = () => this.handleDelete(params);
        this.eGui.appendChild(this.element);
    }

    handleDelete(params) {
        store.dispatch(SettingsActions.RemoveFund(params.data));
    }

    getGui() {
        return this.eGui;
    }
}

export default () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    const [newSettings, setNewSettings] = useState({});
    const [newSettingsGridApi, setNewSettingsGridApi] = useState(null);
    const [gridApi, setGridApi] = useState(null);
    const [errors, setErrors] = useState(null);
    const [refreshTable, setRefreshTable] = useState(null);
    const showErrors = useSelector(SettingsSelectors.DisplayFundErrors);
    const fund = useSelector(SettingsSelectors.FundSettings);
    const { "enable-multi-account-code-support": isAccountSelectable } =
        useSelector(SettingsSelectors.FeatureFlags);

    const accounts = useSelector(SettingsSelectors.GeneralAccountSettings);

    const createFundColumnDefs = useMemo(
        () => getCreateFundColumnDefs({ isAccountSelectable }),
        [isAccountSelectable]
    );

    const detailFundColumnDefs = useMemo(
        () => getDetailFundColumnDefs({ isAccountSelectable }),
        [isAccountSelectable]
    );

    useEffect(() => {
        return () => dispatch(SettingsActions.SetDisplayNewFundErrors(false));
    }, [dispatch]);

    const addFund = () => {
        if (newSettingsGridApi) newSettingsGridApi.stopEditing();
        if (!empty(newSettings)) {
            dispatch(SettingsActions.AddFundSettings(newSettings))
                .then(() => {
                    setNewSettings({});
                    setRefreshTable(true);
                })
                .catch((e) => {
                    newSettingsGridApi.redrawRows();
                });
        }
    };

    const handleRowDataChanged = (e) => {
        if (!refreshTable || !gridApi || empty(fund)) return;

        const index = fund.length - 1;
        gridApi.ensureIndexVisible(index, "bottom");
        setRefreshTable(false);
    };

    const handleNewGridReady = (params) => {
        setNewSettingsGridApi(params.api);
    };
    const handleGridReady = (params) => {
        setGridApi(params.api);
    };

    const handleCellValueChange = (params) => {
        setErrors(
            Validators.getFundValidationErrors(
                params.data,
                fund,
                isAccountSelectable
            )
        );
    };

    return (
        <div className={styles.fundSettings}>
            <div className={styles.addSettings} data-testid="new-fund-setting">
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        onGridReady={handleNewGridReady}
                        rowData={[newSettings]}
                        defaultColDef={fundDefaultColDef}
                        columnDefs={createFundColumnDefs}
                        onCellValueChanged={handleCellValueChange}
                        singleClickEdit
                        suppressContextMenu={true}
                        context={{ accounts }}
                    />
                </div>
            </div>
            <div className={styles.bottom}>
                {showErrors && !empty(errors) ? (
                    <span
                        className={styles.errorMessage}
                        data-testid={"fund-settings-error-message"}
                    >
                        {FUND_SETTING_ERRORS[errors[0]]}
                    </span>
                ) : (
                    !isAccountSelectable && (
                        <div className={styles.message}>
                            <div className={styles.rect} />
                            Either SLB Num or Fund Code must be entered
                        </div>
                    )
                )}
                <Button
                    onClick={addFund}
                    disabled={
                        empty(newSettings) || (showErrors && !empty(errors))
                    }
                    color="reversed"
                    dataTestId="add-fund-settings"
                    value="Add"
                />
            </div>
            <div className={styles.fund} data-testid="fund-settings">
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        rowData={fund}
                        onGridReady={handleGridReady}
                        getRowId={(params) => params.data.fundName}
                        defaultColDef={fundDefaultColDef}
                        columnDefs={detailFundColumnDefs}
                        components={{ deleteButton: DeleteButton }}
                        enableCellChangeFlash={true}
                        onRowDataChanged={handleRowDataChanged}
                        suppressScrollOnNewData={true}
                        suppressContextMenu={true}
                        scrollbarWidth={8}
                        suppressHorizontalScroll={true}
                    />
                </div>
            </div>
            <div className={`${styles.bottom} ${styles.buttons}`}>
                <Button
                    onClick={() =>
                        dispatch(SettingsActions.updateFundSettings())
                    }
                    dataTestId="save-fund-settings"
                    value="Save"
                    disabled={!isTraderRole}
                />
                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    color="reversedWhite"
                    dataTestId="cancel-fund-settings"
                    value="Cancel"
                />
            </div>
        </div>
    );
};
